import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SparkleIcon, XIcon, CheckCircleIcon, TruckIcon, StorefrontIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { sortByUnit } from "@/lib/unitSort";

const CATEGORIES = ["engine", "transmission", "tires", "brakes", "electrical", "body", "oil_change", "annual_safety", "reefer", "trailer", "other"];

/**
 * Review dialog for AI-parsed repair invoices / work orders.
 * User edits, then confirms — we POST to /repairs.
 */
export default function AiRepairReview({ open, onOpenChange, parsed, trucks, trailers = [], shops, repairType, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    asset_kind: parsed?.matched_trailer ? "trailer" : "truck",
    truck_id: parsed?.matched_truck?.id || "",
    trailer_id: parsed?.matched_trailer?.id || "",
    shop_id: parsed?.matched_shop?.id || "",
    repair_type: parsed?.repair_type || "outside",
    title: parsed?.extracted?.summary || "",
    description: (parsed?.extracted?.line_items || []).map((l) => `${l.description || ""}${l.qty ? ` × ${l.qty}` : ""}${l.amount ? ` — $${l.amount}` : ""}`).filter(Boolean).join("\n"),
    category: parsed?.extracted?.category || "other",
    repair_date: parsed?.extracted?.invoice_date || new Date().toISOString().slice(0, 10),
    mileage: parsed?.extracted?.mileage || "",
    parts_cost: 0,
    labor_cost: parsed?.extracted?.labor_cost || 0,
    labor_hours: parsed?.extracted?.labor_hours || 0,
    shop_bill: parsed?.extracted?.total || 0,
    invoice_number: parsed?.extracted?.invoice_number || "",
    mechanic_name: "",
    parts_used: parsed?.extracted?.parts_used || [],
    currency: parsed?.extracted?.currency === "USD" ? "USD" : "CAD",
    notes: `AI-parsed from ${parsed?.filename || "invoice"}. ${parsed?.extracted?.shop_address || ""}`.trim(),
  }));

  // Reset when parsed changes
  React.useEffect(() => {
    if (!parsed) return;
    setForm({
      asset_kind: parsed.matched_trailer ? "trailer" : "truck",
      truck_id: parsed.matched_truck?.id || "",
      trailer_id: parsed.matched_trailer?.id || "",
      shop_id: parsed.matched_shop?.id || "",
      repair_type: parsed.repair_type || "outside",
      title: parsed.extracted?.summary || "",
      description: (parsed.extracted?.line_items || []).map((l) => `${l.description || ""}${l.qty ? ` × ${l.qty}` : ""}${l.amount ? ` — $${l.amount}` : ""}`).filter(Boolean).join("\n"),
      category: parsed.extracted?.category || "other",
      repair_date: parsed.extracted?.invoice_date || new Date().toISOString().slice(0, 10),
      mileage: parsed.extracted?.mileage || "",
      parts_cost: 0,
      labor_cost: parsed.extracted?.labor_cost || 0,
      labor_hours: parsed.extracted?.labor_hours || 0,
      shop_bill: parsed.extracted?.total || 0,
      invoice_number: parsed.extracted?.invoice_number || "",
      mechanic_name: "",
      parts_used: parsed.extracted?.parts_used || [],
      currency: parsed.extracted?.currency === "USD" ? "USD" : "CAD",
      notes: `AI-parsed from ${parsed.filename || "invoice"}. ${parsed.extracted?.shop_address || ""}`.trim(),
    });
  }, [parsed]);

  const save = async () => {
    if (form.asset_kind === "truck" && !form.truck_id) {
      toast.error("Please select a truck");
      return;
    }
    if (form.asset_kind === "trailer" && !form.trailer_id) {
      toast.error("Please select a trailer");
      return;
    }
    if (!form.title) {
      toast.error("Please add a title / summary");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        truck_id: form.asset_kind === "truck" ? form.truck_id : null,
        trailer_id: form.asset_kind === "trailer" ? form.trailer_id : null,
        repair_type: form.repair_type,
        shop_id: form.repair_type === "outside" ? form.shop_id || null : null,
        title: form.title,
        description: form.description,
        category: form.category,
        repair_date: form.repair_date,
        mileage: form.mileage ? Number(form.mileage) : null,
        parts_used: form.parts_used || [],
        parts_cost: Number(form.parts_cost) || 0,
        labor_cost: Number(form.labor_cost) || 0,
        labor_hours: Number(form.labor_hours) || 0,
        shop_bill: Number(form.shop_bill) || 0,
        mechanic_name: form.mechanic_name || "",
        invoice_number: form.invoice_number || "",
        currency: form.currency === "USD" ? "USD" : "CAD",
        notes: form.notes || "",
        attachments: [],
      };
      await api.post("/repairs", payload);
      toast.success("Repair saved");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!parsed) return null;
  const isOutside = form.repair_type === "outside";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="ai-repair-review">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <SparkleIcon size={20} weight="fill" className="text-blue-600" />
            Review AI-extracted {isOutside ? "invoice" : "work order"}
            <span className="text-xs font-mono text-slate-500 ml-2">{parsed.filename}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 mb-3">
          Everything below is editable. Double-check details before saving.
        </div>

        {/* AI match cues */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm">
            <div className="text-[10px] uppercase font-mono text-slate-500 font-bold mb-1 flex items-center gap-1">
              <TruckIcon size={12} weight="bold" /> Asset (AI unit# = {parsed.extracted?.unit_number || "—"})
            </div>
            {parsed.matched_truck ? (
              <div className="text-sm">Matched to truck <b>#{parsed.matched_truck.unit_number}</b> {parsed.matched_truck.make} {parsed.matched_truck.model}</div>
            ) : parsed.matched_trailer ? (
              <div className="text-sm">Matched to trailer <b>#{parsed.matched_trailer.unit_number}</b> {(parsed.matched_trailer.trailer_type || "").replace(/_/g, " ")}</div>
            ) : (
              <div className="text-xs text-amber-700">No asset matched — pick a truck or trailer below</div>
            )}
          </div>
          {isOutside && (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm">
              <div className="text-[10px] uppercase font-mono text-slate-500 font-bold mb-1 flex items-center gap-1">
                <StorefrontIcon size={12} weight="bold" /> Shop (AI = {parsed.extracted?.shop_name || "—"})
              </div>
              {parsed.matched_shop ? (
                <div className="text-sm">Matched to <b>{parsed.matched_shop.name}</b></div>
              ) : (
                <div className="text-xs text-amber-700">New shop — pick existing or leave blank to skip</div>
              )}
            </div>
          )}
        </div>

        {/* Currency — auto-detected from shop address, editable */}
        <div className="mb-4 p-3 bg-blue-50/50 border border-blue-200 rounded-sm flex items-center gap-4" data-testid="airr-currency-row">
          <div>
            <div className="text-[10px] uppercase font-mono text-blue-700 font-bold">Invoice currency</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Auto-detected from shop address — override if wrong</div>
          </div>
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              data-testid="airr-currency-cad"
              onClick={() => setForm({ ...form, currency: "CAD" })}
              className={`px-4 py-2 border-2 rounded-sm text-sm font-bold font-mono transition-colors ${form.currency === "CAD" ? "border-slate-800 bg-slate-800 text-white" : "border-slate-300 hover:bg-slate-100 text-slate-700"}`}
            >CA$ CAD</button>
            <button
              type="button"
              data-testid="airr-currency-usd"
              onClick={() => setForm({ ...form, currency: "USD" })}
              className={`px-4 py-2 border-2 rounded-sm text-sm font-bold font-mono transition-colors ${form.currency === "USD" ? "border-emerald-700 bg-emerald-700 text-white" : "border-slate-300 hover:bg-slate-100 text-slate-700"}`}
            >US$ USD</button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider font-bold">Vehicle type *</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, asset_kind: "truck", trailer_id: "" })}
                data-testid="airr-asset-truck"
                className={`px-3 py-2 border-2 rounded-sm text-sm font-semibold transition-colors ${form.asset_kind === "truck" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 hover:bg-slate-50"}`}
              >Truck</button>
              <button
                type="button"
                onClick={() => setForm({ ...form, asset_kind: "trailer", truck_id: "" })}
                data-testid="airr-asset-trailer"
                className={`px-3 py-2 border-2 rounded-sm text-sm font-semibold transition-colors ${form.asset_kind === "trailer" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 hover:bg-slate-50"}`}
              >Trailer</button>
            </div>
          </div>
          {form.asset_kind === "truck" ? (
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Truck *</Label>
              <Select value={form.truck_id} onValueChange={(v) => setForm({ ...form, truck_id: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="airr-truck"><SelectValue placeholder="Pick a truck" /></SelectTrigger>
                <SelectContent>
                  {sortByUnit(trucks).map((t) => (
                    <SelectItem key={t.id} value={t.id}>#{t.unit_number} · {t.make} {t.model}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Trailer *</Label>
              <Select value={form.trailer_id} onValueChange={(v) => setForm({ ...form, trailer_id: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="airr-trailer"><SelectValue placeholder="Pick a trailer" /></SelectTrigger>
                <SelectContent>
                  {trailers.length === 0 && <div className="p-3 text-xs text-slate-500">No trailers yet — add one under Trailers first</div>}
                  {sortByUnit(trailers).map((t) => (
                    <SelectItem key={t.id} value={t.id}>#{t.unit_number} · {(t.trailer_type || "").replace(/_/g, " ")} {t.make ? "· " + t.make : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {isOutside && (
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Repair Shop</Label>
              <Select value={form.shop_id} onValueChange={(v) => setForm({ ...form, shop_id: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="airr-shop"><SelectValue placeholder="Pick a shop (optional)" /></SelectTrigger>
                <SelectContent>
                  {shops.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider font-bold">Title / Summary *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-sm" data-testid="airr-title" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="airr-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold">Date</Label>
            <Input type="date" value={form.repair_date} onChange={(e) => setForm({ ...form, repair_date: e.target.value })} className="mt-1 rounded-sm" data-testid="airr-date" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold">Mileage</Label>
            <Input value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} className="mt-1 rounded-sm font-mono" data-testid="airr-mileage" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold">Invoice #</Label>
            <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="mt-1 rounded-sm font-mono" data-testid="airr-invoice" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold">Labor Cost</Label>
            <Input type="number" step="0.01" value={form.labor_cost} onChange={(e) => setForm({ ...form, labor_cost: e.target.value })} className="mt-1 rounded-sm font-mono text-right" data-testid="airr-labor" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold">{isOutside ? "Shop Bill (Total)" : "Total"}</Label>
            <Input type="number" step="0.01" value={form.shop_bill} onChange={(e) => setForm({ ...form, shop_bill: e.target.value })} className="mt-1 rounded-sm font-mono text-right font-bold text-blue-700" data-testid="airr-total" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider font-bold">Work Performed / Line items (AI-parsed)</Label>
            <Textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm font-mono text-xs" data-testid="airr-desc" />
          </div>
          {form.parts_used?.length > 0 && (
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold">Parts detected on invoice</Label>
              <div className="mt-1 border border-slate-200 rounded-sm divide-y divide-slate-200 text-xs">
                {form.parts_used.map((p, i) => (
                  <div key={i} className="p-2 flex items-center gap-3">
                    <div className="flex-1 font-mono">{p.part_number || "—"} · {p.name || "part"}</div>
                    <div className="w-16 text-right font-mono">×{p.quantity || 1}</div>
                    <div className="w-24 text-right font-mono">${(p.unit_cost || 0).toFixed(2)}</div>
                    <button type="button" onClick={() => setForm({ ...form, parts_used: form.parts_used.filter((_, idx) => idx !== i) })} className="text-red-600" data-testid={`airr-part-remove-${i}`}>
                      <XIcon size={12} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold"
            data-testid="airr-save"
          >
            <CheckCircleIcon size={16} weight="bold" className="mr-2" />
            {saving ? "Saving..." : "Save Repair"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
